import {useCallback, useRef} from 'react';
import {useLocation, useMatch, useNavigate} from 'react-router-dom';
import cn from 'clsx';
import {useTranslation} from 'react-i18next';
import {useReservation} from 'context/reservation';
import {useCustomTheme} from 'context/customTheme';
import {useSummary} from 'context/summary';
import {useAuth} from 'hooks/auth/useAuth';
import {GUESTAPP_NAVIGATION_MODE} from 'utils/constants';
import {PATHS} from 'Routes';
import chekinDefaultSplashLogo from 'assets/icons/chekin-default-splash-logo.svg?no-inline';
import {NavLinks, useNavigationLinks, usePath, useSideBarLinks} from 'hooks';
import {useOnboardingStore} from 'pages/Auth/store/useOnboardingStore';
import {SideBar} from 'components/SideBar';
import {
  LanguageSelection,
  LanguageSelectionRef,
} from 'components/ModalSheets/LanguageSelection/LanguageSelection';
import {
  DiscardChangesModal,
  DiscardChangesModalRef,
} from 'components/common/DiscardChangesModal/DiscardChangesModal';
import {Container, Description, Label, PageSideBarItem, StyledItem} from './styled';

type Props = {
  header?: boolean;
  closeMobileMenu?: () => void;
};

type AppHeaderSettings = {
  navigationTitleConfig: GUESTAPP_NAVIGATION_MODE;
  logo?: string;
};

function getHousingImgSrc(appSettingTemplate: AppHeaderSettings, housingLogo?: string) {
  const isLogoConfigured = [
    GUESTAPP_NAVIGATION_MODE.logo,
    GUESTAPP_NAVIGATION_MODE.property_name_and_logo,
  ].includes(appSettingTemplate.navigationTitleConfig);

  const showLogo = !appSettingTemplate?.logo || isLogoConfigured;

  if (!showLogo) {
    return;
  }

  return appSettingTemplate.logo || housingLogo;
}

const getRedirectPatch = (label: NavLinks) => {
  switch (label) {
    case NavLinks.paymentCart:
      return PATHS.payments.main;
    case NavLinks.virtualKeys:
      return PATHS.remoteAccess;
    default:
      return PATHS.home;
  }
};

function PageSideBar({header, closeMobileMenu}: Props) {
  const {t} = useTranslation();
  const {housing} = useReservation();
  const {data: template} = useCustomTheme();
  const {isLoggedIn, logout} = useAuth();
  const {getMainPath, getFullPath} = usePath();
  const {navItems} = useSideBarLinks();
  const {data, isInstantCheckInEnabled} = useSummary();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const discardChangesModalRef = useRef<DiscardChangesModalRef>(null);
  const languageSelectionModalRef = useRef<LanguageSelectionRef>(null);
  const {resetState} = useOnboardingStore();

  const handleAuthClick = () => {
    if (isLoggedIn) {
      logout();
      resetState();
      closeMobileMenu?.();
      navigate(getFullPath(PATHS.home), {replace: true, state: {isLoggingOut: true}});
    } else {
      navigate(getFullPath(PATHS.auth.login));
    }
  };

  const {navigatorLinksArray} = useNavigationLinks({
    onLanguageClick: () => languageSelectionModalRef?.current?.open(),
    onAuthClick: handleAuthClick,
    hasAuth: isInstantCheckInEnabled,
  });

  const titleLogo = template?.navigation_title_logo || chekinDefaultSplashLogo;
  const titleLogoType =
    template?.navigation_title_type || GUESTAPP_NAVIGATION_MODE.property_name;

  const isAddPersonalDataForm = useMatch(getMainPath(PATHS.addPersonalDataForm));
  const isMainIvView = useMatch('/:token/verification');
  const isIdentityVerificationView = pathname.includes(PATHS.verification.main);
  const isAddFormOrIvView =
    isAddPersonalDataForm || (!isMainIvView && isIdentityVerificationView);
  const showHeader = header ?? true;
  const name = data?.display_name ?? data?.property_name ?? '';
  const AuthNavLinks = [NavLinks.logout, NavLinks.login];

  const handleNavItemClick = useCallback(
    (onClick: () => void, label: NavLinks) => {
      if (isAddFormOrIvView) {
        discardChangesModalRef.current?.open(getRedirectPatch(label));
        return;
      }
      onClick();
    },
    [isAddFormOrIvView, discardChangesModalRef],
  );

  const appSettingTemplate: AppHeaderSettings = {
    navigationTitleConfig: titleLogoType,
    logo: titleLogo ? String(titleLogo) : undefined,
  };

  const housingImg = getHousingImgSrc(appSettingTemplate, housing?.picture?.src);

  const showLogoAndPropertyName = [
    GUESTAPP_NAVIGATION_MODE.property_name,
    GUESTAPP_NAVIGATION_MODE.property_name_and_logo,
  ].includes(titleLogoType);

  const sideBarTitle = showLogoAndPropertyName || !titleLogoType ? name : '';

  const handleClickOnTitle = useCallback(() => {
    return navigate(getFullPath(PATHS.home));
  }, [getFullPath, navigate]);

  return (
    <>
      <Container>
        <SideBar>
          <SideBar.Header
            onlyLogo={template?.navigation_title_type === GUESTAPP_NAVIGATION_MODE.logo}
            imgSrc={housingImg}
            show={showHeader}
            isLoading={false}
          >
            {sideBarTitle && (
              <div onClick={() => handleClickOnTitle()}>{sideBarTitle}</div>
            )}
          </SideBar.Header>

          <SideBar.Body>
            <SideBar.Items>
              {navItems.map((navItem, index) => {
                return (
                  <SideBar.Item
                    isLoading={false}
                    isActive={navItem.selected}
                    customIcon={navItem.icon}
                    onClick={() => handleNavItemClick(navItem.onClick, navItem.label)}
                    key={index}
                    badgeData={navItem.extraData}
                  >
                    <StyledItem>{t(navItem.label)}</StyledItem>
                  </SideBar.Item>
                );
              })}
            </SideBar.Items>
            <SideBar.Items footer>
              {navigatorLinksArray.map((navItem, index) => (
                <SideBar.Item
                  key={index}
                  isActive={false}
                  onClick={navItem.onclick}
                  isLoading={false}
                  className={cn(
                    navItem?.className,
                    AuthNavLinks.includes(navItem.label) && navItem.label,
                  )}
                  customIcon={navItem.icon}
                >
                  <PageSideBarItem>
                    <Label>{t(navItem.label)}</Label>
                    {navItem.description && (
                      <Description>{navItem.description}</Description>
                    )}
                  </PageSideBarItem>
                </SideBar.Item>
              ))}
            </SideBar.Items>
          </SideBar.Body>
        </SideBar>
      </Container>
      <DiscardChangesModal ref={discardChangesModalRef} />
      <LanguageSelection ref={languageSelectionModalRef} />
      {/* <HeaderTooltipOverlay /> */}
    </>
  );
}

export {PageSideBar};
